import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Image from '../../../components/AppImage';
import Icon from '../../../components/AppIcon';
import useFavorites from '../../../hooks/useFavorites';

const BibleCard = ({ product }) => {
  const { toggleFavorite, isProductFavorited, loading } = useFavorites();
  const [isLiked, setIsLiked] = useState(false);

  useEffect(() => {
    if (product?.bible_id) {
      setIsLiked(isProductFavorited(product.bible_id, 'bible'));
    }
  }, [product?.bible_id, isProductFavorited]);

  const truncateAuthor = (authorName) => {
    if (!authorName) return 'Versión desconocida';
    return authorName.length > 10 ? `${authorName.substring(0, 10)}...` : authorName;
  };

  const getDisplayPrice = (product) => {
    // Si tiene ambos (forro y pestañas), mostrar total con ambos
    if (product.has_cover && product.has_tab_index && product.total_with_both) {
      return product.total_with_both;
    }
    // Si solo tiene forro, mostrar total con forro
    if (product.has_cover && product.total_with_cover) {
      return product.total_with_cover;
    }
    // Si solo tiene pestañas, mostrar total con pestañas
    if (product.has_tab_index && product.total_with_tab_index) {
      return product.total_with_tab_index;
    }
    // Si no tiene ninguno, mostrar precio base
    return product.price_bible;
  };

  const renderStars = (rating) => {
    const stars = [];
    const fullStars = Math.floor(rating || 0);
    const hasHalfStar = (rating || 0) % 1 >= 0.5;

    for (let i = 0; i < 5; i++) {
      if (i < fullStars) {
        stars.push(
          <Icon key={i} name="Star" className="h-2.5 w-2.5 fill-yellow-400 text-yellow-400" />
        );
      } else if (i === fullStars && hasHalfStar) {
        stars.push(
          <Icon key={i} name="Star" className="h-2.5 w-2.5 fill-yellow-400/50 text-yellow-400" />
        );
      } else {
        stars.push(
          <Icon key={i} name="Star" className="h-2.5 w-2.5 text-gray-300" />
        );
      }
    }
    return stars;
  };

  useEffect(() => {
    if (product?.bible_id) {
      setIsLiked(isProductFavorited(product.bible_id, 'bible'));
    }
  }, [product?.bible_id, isProductFavorited]);

  const handleLike = async (e) => {
    e?.preventDefault();
    e?.stopPropagation();
    if (!product?.bible_id || loading) return;

    const result = await toggleFavorite(product.bible_id, 'bible');
    if (result.success) {
      setIsLiked(result.isFavorited);
    }
  };

  if (!product) {
    return (
      <div className="animate-pulse bg-muted rounded-lg p-4 h-full flex flex-col space-y-3">
        <div className="bg-muted-foreground/20 rounded aspect-[3/4] w-full" />
        <div className="h-4 bg-muted-foreground/20 rounded w-3/4" />
        <div className="h-3 bg-muted-foreground/10 rounded w-1/2" />
        <div className="h-3 bg-muted-foreground/10 rounded w-2/3" />
      </div>
    );
  }

  return (
    <Link to={`/bible-detail?id=${product.bible_id}`} className="group block">
      <div className="bg-card rounded-lg shadow-subtle hover:shadow-modal transition-gentle hover-lift overflow-hidden h-full flex flex-col sm:min-h-[280px] md:min-h-[320px]">
        <div className="relative aspect-[3/4] overflow-hidden flex-shrink-0 flex items-center justify-center"
             style={{
               backgroundImage: 'url(/assets/images/background_image.png)',
               backgroundSize: 'cover',
               backgroundPosition: 'center',
               backgroundRepeat: 'no-repeat'
             }}>
          <div className="relative w-[80px] h-[110px] flex items-center justify-center">
            <Image
              src={product.image_urls?.[0] || '/placeholder.jpg'}
              alt={`Portada de ${product.title_bible}`}
              className="w-full h-full object-cover rounded shadow-lg"
              low
            />
          </div>
          <button
            onClick={handleLike}
            className={`absolute top-2 right-2 flex h-8 w-8 items-center justify-center rounded-full shadow-modal transition-smooth sm:h-9 sm:w-9 ${
              isLiked
                ? 'bg-accent text-accent-foreground'
                : 'bg-white/90 text-muted-foreground hover:bg-white hover:text-accent'
            }`}
            aria-label="Añadir a favoritos"
          >
            <Icon
              name="Heart"
              className={`h-4 w-4 ${isLiked ? 'fill-current' : ''} sm:h-5 sm:w-5`}
            />
          </button>
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            <div className="bg-green-500 text-white text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full">
              Biblia
            </div>
            {product.is_new_product && (
              <div className="bg-success text-success-foreground text-xs font-bold px-1.5 py-0.5 sm:px-2 sm:py-1 rounded-full">
                Nuevo
              </div>
            )}
          </div>
        </div>

        <div className="p-2 sm:p-3 flex-1 flex flex-col justify-between">
          <div className="space-y-1">
            <h3 className="font-bold text-sm sm:text-base leading-tight line-clamp-2 text-foreground group-hover:text-primary transition-colors">
              {product.title_bible && product.title_bible.length > 17 ? `${product.title_bible.substring(0, 17)}...` : product.title_bible}
            </h3>
            <p className="text-[10px] sm:text-xs text-muted-foreground line-clamp-1">
              {truncateAuthor(product.version_bible)}
            </p>

            {/* Bible-specific information */}
            {product.language_bible && (
              <div className="text-[10px] sm:text-xs text-muted-foreground">
                <span className="bg-green-100 text-green-800 px-1.5 py-0.5 rounded-full">
                  {product.language_bible}
                </span>
              </div>
            )}
          </div>

          <div className="mt-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-xs sm:text-sm font-semibold text-primary">
                ${getDisplayPrice(product)?.toFixed(2) || '0.00'}
              </span>
              {product.available_bible ? (
                <span className="text-[10px] sm:text-xs text-green-600 font-medium">
                  Disponible
                </span>
              ) : (
                <span className="text-[10px] sm:text-xs text-red-500 font-medium">
                  No disponible
                </span>
              )}
            </div>

            {/* Bible version/format info */}
            {product.format_bible && (
              <div className="text-[10px] text-muted-foreground">
                Formato: {product.format_bible}
              </div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
};

export default BibleCard;

